import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, Mail, ArrowRight } from 'lucide-react';
import ConferenceNav from '../../../common/ConferenceNav';
import SEO from '../../../common/SEO';

const UpcomingConference = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <>
      <SEO 
        title="Upcoming Scientific Research Conferences & IEEE Paper Writing"
        description="Stay updated with upcoming Scientific Research conferences, IEEE paper writing deadlines, Scopus paper writing bhopal, and Conference paper writing publication events."
        keywords={[
          "Scientific Research",
          "IEEE paper writing",
          "Conference paper writing publication",
          "Scopus paper writing bhopal",
          "SCI paper writing bhopal",
          "Research paper writing and publication"
        ]}
        canonicalPath="/upcoming-conference"
      />
      <ConferenceNav />
      <div className='min-h-screen  bg-blue-100 '>
        {/* Simple gradient background */}
        <div className='absolute inset-0  ' />

        {/* Content */}
        <div
          className={`relative z-10 min-h-screen flex items-center justify-center p-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className=' mx-auto w-full  mt-16 '>
            {/* Main card */}
            <div className='rounded-lg shadow-lg border w-auto border-gray-200 overflow-hidden'>
              {/* Header section */}
              <div className='bg-gradient-to-r  from-blue-600 to-blue-700 px-8 py-12 text-center text-white'>
                <h1 className='text-4xl md:text-5xl font-bold mb-4'>
                  Upcoming Conference
                </h1>
                <p className='text-xl text-blue-100 max-w-2xl mx-auto'>
                  Join industry leaders and innovators for insights that shape
                  the future
                </p>
              </div>

              {/* Content section */}
              <div className='px-8 py-12'>
                {/* Info cards grid */}
                <div className='grid md:grid-cols-3 gap-6 mb-12'>
                  {/* Date card */}
                  <div className='text-center p-6 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-300'>
                    <Calendar className='w-8 h-8 text-blue-600 mx-auto mb-4' />
                    <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                      Date
                    </h3>
                    <p className='text-gray-600'>To be announced</p>
                    <p className='text-sm text-gray-500 mt-1'>
                      Stay tuned for updates
                    </p>
                  </div>

                  {/* Location card */}
                  <div className='text-center p-6 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-300'>
                    <MapPin className='w-8 h-8 text-blue-600 mx-auto mb-4' />
                    <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                      Location
                    </h3>
                    <p className='text-gray-600'>Premium venue</p>
                    <p className='text-sm text-gray-500 mt-1'>
                      Details coming soon
                    </p>
                  </div>

                  {/* Attendees card */}
                  <div className='text-center p-6 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-300'>
                    <Users className='w-8 h-8 text-blue-600 mx-auto mb-4' />
                    <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                      Attendees
                    </h3>
                    <p className='text-gray-600'>Industry professionals</p>
                    <p className='text-sm text-gray-500 mt-1'>
                      Networking opportunities
                    </p>
                  </div>
                </div>

                {/* Main content */}
                <div className='bg-gray-50 rounded-lg p-8 mb-8 border border-gray-200'>
                  <h2 className='text-2xl font-bold text-gray-900 mb-6'>
                    Why Attend Our Conference?
                  </h2>

                  <div className='space-y-4 text-gray-700'>
                    <p className='text-lg leading-relaxed'>
                      This conference brings together thought leaders,
                      innovators, and professionals to explore the latest trends
                      and insights in our industry. Gain valuable knowledge,
                      expand your network, and discover opportunities for
                      growth.
                    </p>

                    <p className='leading-relaxed'>
                      Whether you&apos;re looking to learn about emerging
                      technologies, connect with peers, or gain strategic
                      insights for your organization, this event is designed to
                      provide meaningful value for all attendees.
                    </p>
                  </div>

                  {/* Benefits list */}
                  <div className='mt-8 grid md:grid-cols-2 gap-4'>
                    <div className='flex items-start space-x-3'>
                      <div className='w-2 h-2 bg-blue-600 rounded-full mt-2'></div>
                      <span className='text-gray-700'>
                        Expert keynote speakers
                      </span>
                    </div>
                    <div className='flex items-start space-x-3'>
                      <div className='w-2 h-2 bg-blue-600 rounded-full mt-2'></div>
                      <span className='text-gray-700'>
                        Interactive workshops
                      </span>
                    </div>
                    <div className='flex items-start space-x-3'>
                      <div className='w-2 h-2 bg-blue-600 rounded-full mt-2'></div>
                      <span className='text-gray-700'>Networking sessions</span>
                    </div>
                    <div className='flex items-start space-x-3'>
                      <div className='w-2 h-2 bg-blue-600 rounded-full mt-2'></div>
                      <span className='text-gray-700'>Industry insights</span>
                    </div>
                  </div>
                </div>

                {/* CTA section */}
                <div className='text-center space-y-4'>
                  <h3 className='text-xl font-semibold text-gray-900 mb-6'>
                    Don&apos;t miss out on this opportunity
                  </h3>

                  <div className='flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto'>
                    <button className='flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg'>
                      <Mail className='w-5 h-5' />
                      <span>Get Updates</span>
                    </button>

                    <button className='flex-1 flex items-center justify-center gap-2 py-3 px-6 border border-gray-300 text-gray-700 font-medium rounded-lg hover:border-blue-600 hover:text-blue-600 transition-all duration-300'>
                      <span>Learn More</span>
                      <ArrowRight className='w-5 h-5' />
                    </button>
                  </div>

                  <p className='text-sm text-gray-500 mt-6'>
                    We&apos;ll notify you as soon as registration opens and
                    event details are finalized.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpcomingConference;
